import { ApiOutlined, ArrowLeftOutlined, HistoryOutlined, UserOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Card, Descriptions, Divider, message, Space, Spin, Table, Tag, Typography } from "antd";
import dayjs from 'dayjs';
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";
import { getSharingHistory, getSubmissionDetails } from "./sharedSubmissionsService";

const { Title } = Typography;

const SubmissionView = () => {
  const [submission, setSubmission] = useState(null);
  const [sharingHistory, setSharingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  useEffect(() => {
    const loadSubmissionDetails = async () => {
      try {
        setLoading(true);
        
        // Try to get submission from navigation state first
        if (location.state?.submission) {
          setSubmission(location.state.submission);
          if (location.state.submission.HasSharing) {
            await loadSharingHistory(location.state.submission.SubmissionId);
          }
        } else if (id) {
          // Fallback to API call if no state data
          const details = await getSubmissionDetails(id);
          setSubmission(details);
          if (details.HasSharing) {
            await loadSharingHistory(id);
          }
        } else {
          message.error("No submission data found. Please go back and select a submission.");
          navigate("/settings?tab=3&subtab=3");
        }
      } catch (err) {
        message.error("Failed to load submission details");
        console.error('Error loading submission:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubmissionDetails();
  }, [id, location.state, navigate]);

  const loadSharingHistory = async (submissionId) => {
    try {
      setHistoryLoading(true);
      const history = await getSharingHistory(submissionId);
      setSharingHistory(history || []);
    } catch (err) {
      console.error('Error loading sharing history:', err);
      setSharingHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/settings?tab=3&subtab=3");
  };

  if (loading) {
    return (
      <AdminAppLayout>
        <div
           className="page-content-wrapper"
           style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading submission details...</div>
        </div>
      </AdminAppLayout>
    );
  }

  if (!submission) {
    return (
      <AdminAppLayout>
        <div className="page-content-wrapper">
        {/*<div style={{ paddingLeft: 152, paddingRight: 152, marginTop: 40 }}>*/}
          <div>Submission not found</div>
        </div>
      </AdminAppLayout>
    );
  }

  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'Date',
      key: 'date',
      render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm'),
      width: 150,
    },
    {
      title: 'Action',
      dataIndex: 'Action',
      key: 'action',
      render: (action) => (
        <Tag color={action === 'Shared' ? 'green' : action === 'Reversed' ? 'red' : 'blue'}>
          {action}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Points',
      dataIndex: 'SharedPoints',
      key: 'points',
      render: (points) => points?.toFixed(2) || '0.00',
      width: 100,
    },
    {
      title: 'Percentage',
      dataIndex: 'SharePercentage',
      key: 'percentage',
      render: (percentage) => `${percentage?.toFixed(2) || '0.00'}%`,
      width: 100,
    },
    {
      title: 'Shareholder',
      dataIndex: 'ShareholderAgentName',
      key: 'shareholder',
    },
    {
      title: 'Notes',
      dataIndex: 'Notes',
      key: 'notes',
      render: (notes) => notes || '-',
    },
  ];

  return (
    <AdminAppLayout>
      <div style={{ paddingLeft: 152, paddingRight: 152, marginTop: 40 }}>
        <Breadcrumb
          items={[
            { title: "Settings" },
            { title: "Points Transfer" },
            { title: "Shared Submissions" },
            { title: "View Submission" },
          ]}
        />
        <Title level={4} style={{ color: colors.black, marginTop: 3 }}>
          Submission Details
        </Title>
        <Divider type="horizontal" />
        
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            Back to Shared Submissions
          </Button>
          
          
        </Space>

        {/* Submission Information */}
        <Card title="Submission Information" style={{ marginBottom: 24 }}>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Policy Name" span={2}>
              <strong>{submission.PolicyName}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Writing Agent">
              {submission.WritingAgentName}
            </Descriptions.Item>
            <Descriptions.Item label="Agency">
              {submission.AgencyName}
            </Descriptions.Item>
            <Descriptions.Item label="Policy Date">
              {dayjs(submission.PolicyDate).format('MMM DD, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Submission Date">
              {dayjs(submission.SubmissionDate).format('MMM DD, YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Premium">
              <strong>${(submission.Premium || 0).toLocaleString('en-US', { 
                minimumFractionDigits: 2,
                maximumFractionDigits: 2 
              })}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Total Points">
              <strong>{submission.SubmissionPoints?.toFixed(2) || '0.00'}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Sharing Status">
              {!submission.HasSharing || submission.SharingType === "None" ? (
                <Tag color="default">Not Shared</Tag>
              ) : submission.SharingType === "Manual" ? (
                <Tag color="blue" icon={<UserOutlined />}>Manual Sharing</Tag>
              ) : submission.SharingType === "Rule" ? (
                <Tag color="green" icon={<ApiOutlined />}>Auto Rule Sharing</Tag>
              ) : (
                <Tag color="green">Shared</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Current Sharing Information */}
        {submission.HasSharing && (
          <Card 
            title={
              <Space>
                {submission.SharingType === "Manual" ? <UserOutlined /> : <ApiOutlined />}
                {submission.SharingType === "Manual" ? "Manual Sharing Details" : "Auto Rule Sharing Details"}
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Sharing Type">
                {submission.SharingType === "Manual" ? (
                  <Tag color="blue" icon={<UserOutlined />}>Manual Sharing</Tag>
                ) : (
                  <Tag color="green" icon={<ApiOutlined />}>Auto Rule Sharing</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Shareholder Agent">
                {submission.ShareholderAgentName}
              </Descriptions.Item>
              <Descriptions.Item label="Share Percentage">
                <strong>{submission.SharePercentage?.toFixed(2) || '0.00'}%</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Shared Points">
                <strong>{submission.SharedPoints?.toFixed(2) || '0.00'}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Remaining Points">
                <strong>{((submission.SubmissionPoints || 0) - (submission.SharedPoints || 0)).toFixed(2)}</strong>
              </Descriptions.Item>
              {submission.SharingType === "Rule" && submission.RuleId && (
                <Descriptions.Item label="Applied Rule ID">
                  <strong>#{submission.RuleId}</strong>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Notes" span={2}>
                {submission.SharingNotes || '-'}
              </Descriptions.Item>
            </Descriptions>
            
            {/* Rule Information Section */}
            {submission.SharingType === "Rule" && (
              <>
                <Divider orientation="left" style={{ marginTop: 24, marginBottom: 16 }}>
                  <Space>
                    <ApiOutlined />
                    Rule Information
                  </Space>
                </Divider>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Rule Type">
                    <Tag color="green">Automatic Points Sharing Rule</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Rule ID">
                    <strong>#{submission.RuleId}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Applied Date">
                    {submission.SharingCreatedDate ? dayjs(submission.SharingCreatedDate).format('MMM DD, YYYY h:mm A') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Description">
                    This submission was automatically shared based on active points sharing rules for this agent's agency. 
                    The rule automatically transfers {submission.SharePercentage?.toFixed(2) || '0'}% of points to the designated recipient agent.
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </Card>
        )}

        {/* Sharing History */}
        {submission.HasSharing && (
          <Card 
            title={
              <Space>
                <HistoryOutlined />
                Sharing History
              </Space>
            }
          >
            <Table
              columns={historyColumns}
              dataSource={sharingHistory}
              loading={historyLoading}
              rowKey={(record, index) => `${record.Date}-${index}`}
              pagination={false}
              size="small"
              locale={{
                emptyText: 'No sharing history found'
              }}
            />
          </Card>
        )}
      </div>
    </AdminAppLayout>
  );
};

export default SubmissionView;
